export interface AppRegistryConfig {
  id: string;
  name: string;
  description: string;
  icon: string;
  path: string;
  port: number;
  status: 'active' | 'maintenance' | 'coming-soon';
  color: string;
  permissions?: string[];
  // Served by the shell (port 3000 in dev) at `path`, not by its own micro-app.
  shellHosted?: boolean;
  // Access comes from this feature flag instead of the user's app_access.
  featureFlag?: string;
}

// Local dev serves every micro-app on a base port in the 3000–3007 range. A worktree made by
// scripts/wt.sh packs its apps onto consecutive ports of its own band (shell 20640, CRM 20641,
// …) and exports that map as VITE_DEV_APP_PORTS ("shell:20640,crm:20641,…"); cross-app links
// read it. Without the map, a port's offset from 3000 in steps of 100 is the band (3001, 4101).
const DEV_BASE_PORT = 3000;
const SHELL_ID = 'shell';

const worktreePorts = (): Map<string, number> =>
  new Map(
    String(import.meta.env?.VITE_DEV_APP_PORTS ?? '')
      .split(',')
      .map((entry: string) => entry.split(':'))
      .filter(([id, port]) => id && Number(port) > 0)
      .map(([id, port]) => [id, Number(port)] as const)
  );

// The band offset baked into a served port (0 on the main checkout / default ports).
const devBandOffset = (port: number): number => {
  const rel = port - DEV_BASE_PORT;
  return rel < 0 ? 0 : rel - (rel % 100);
};
// A port is a dev app port iff it is on the worktree's map, or sits at a known app lane (0–7)
// at or above 3000: the default ports 3000–3007 (offset 0) or a 100-wide band (4100–4107).
const isDevAppPort = (port: number): boolean =>
  [...worktreePorts().values()].includes(port) ||
  (port >= DEV_BASE_PORT && (port - DEV_BASE_PORT) % 100 <= 7);

/** The port an app (or the shell, at base port 3000) is served on, seen from `currentPort`. */
const devPortOf = (appId: string, basePort: number, currentPort: number): number =>
  worktreePorts().get(appId) ?? basePort + devBandOffset(currentPort);

const isLocalDev = (): { currentPort: number } | null => {
  if (typeof window === 'undefined') return null;
  const { hostname, port } = window.location;
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') return null;
  const currentPort = Number(port);
  return isDevAppPort(currentPort) ? { currentPort } : null;
};

export const appRegistry = {
  apps: [
    {
      id: 'prospecting',
      name: 'Prospecting',
      description: 'Lead generation',
      icon: 'UserSearchIcon',
      path: '/prospecting',
      port: 3002,
      status: 'active' as const,
      color: '#4C1A5A',
    },
    {
      id: 'crm',
      name: 'CRM',
      description: 'Customer manager',
      icon: 'Building2Icon',
      path: '/crm',
      port: 3001,
      status: 'active' as const,
      color: '#11413E',
    },
    {
      id: 'success',
      name: 'Success',
      description: 'Customer success',
      icon: 'SuccessIcon',
      path: '/success',
      port: 3003,
      status: 'active' as const,
      color: '#F59E0B',
    },
    {
      id: 'routing',
      name: 'Routing',
      description: 'Routes hub',
      icon: 'CommandIcon',
      path: '/routing',
      port: 3006,
      status: 'active' as const,
      color: '#EF4444',
    },
    {
      id: 'driver',
      name: 'Driver',
      description: 'Driver mobile routes',
      icon: 'TruckIcon',
      path: '/driver-routing',
      port: 3007,
      status: 'active' as const,
      color: '#0F766E',
    },
    {
      id: 'global-search',
      name: 'Global Search',
      description: 'Find companies and people',
      icon: 'SearchIcon',
      path: '/global-search',
      port: DEV_BASE_PORT,
      status: 'active' as const,
      color: 'hsl(var(--primary))',
      shellHosted: true,
      featureFlag: 'global_search_v2',
    },
  ] as AppRegistryConfig[],

  getCurrentApp: (): AppRegistryConfig | undefined => {
    const path = window.location.pathname;
    const matchesPath = (app: AppRegistryConfig) =>
      Boolean(app.path) && (path === app.path || path.startsWith(`${app.path}/`));

    // In development, identify app by port
    const isLocalhost =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    if (isLocalhost) {
      // Shell-hosted apps share the shell's port, so only their path identifies them.
      const shellHostedApp = appRegistry.apps.find((app) => app.shellHosted && matchesPath(app));
      if (shellHostedApp) return shellHostedApp;

      const currentPort = Number.parseInt(window.location.port, 10) || 80;
      // Match against the worktree's port so the right app resolves on a band port.
      return appRegistry.apps.find(
        (app) => !app.shellHosted && devPortOf(app.id, app.port, currentPort) === currentPort
      );
    }

    // In production, use path-based detection
    return appRegistry.apps.find(matchesPath);
  },

  getAppById: (appId: string): AppRegistryConfig | undefined => {
    return appRegistry.apps.find((app) => app.id === appId);
  },

  getAppUrl: (appId: string, subPath?: string): string => {
    const app = appRegistry.apps.find((a) => a.id === appId);

    if (!app) {
      console.log('App not found, returning /');
      return '/';
    }

    // Normalize subPath to ensure it always starts with '/' when present
    const normalizePath = (path: string | undefined): string => {
      if (!path) return '/';
      return path.startsWith('/') ? path : `/${path}`;
    };
    const normalizedSubPath = normalizePath(subPath);

    const dev = isLocalDev();
    if (dev) {
      // Navigate to the target app on the SAME worktree band (shell 20640 → prospecting
      // 20642), not its default port (3002).
      const { currentPort } = dev;
      const targetPort = devPortOf(app.id, app.port, currentPort);
      const isSameApp = currentPort === targetPort;

      // In development, each app runs on its own port
      // If navigating within the same app, use relative path with app prefix
      // If navigating to different app, use full URL with port and app path prefix
      const url = isSameApp
        ? `${app.path}${normalizedSubPath}`
        : `http://localhost:${targetPort}${app.path}${normalizedSubPath}`;
      return url;
    }

    // In production, use path-based URLs with app path prefix
    const url = `${app.path}${normalizedSubPath}`;
    return url;
  },

  /** A path in the shell (the platform home), on the current worktree's band in development. */
  getShellUrl: (subPath = '/'): string => {
    const dev = isLocalDev();
    if (!dev) return subPath;
    return `http://localhost:${devPortOf(SHELL_ID, DEV_BASE_PORT, dev.currentPort)}${subPath}`;
  },

  getActiveApps: (): AppRegistryConfig[] => {
    return appRegistry.apps.filter((app) => app.status === 'active');
  },

  getUserApps: (appAccess?: string[], flags?: Record<string, boolean>): AppRegistryConfig[] => {
    return appRegistry.apps.filter((app) => {
      if (app.status !== 'active') {
        return app.status === 'coming-soon';
      }

      if (app.featureFlag) {
        return flags?.[app.featureFlag] ?? false;
      }

      if (!appAccess || appAccess.length === 0) {
        return true;
      }

      return appAccess.includes(app.id);
    });
  },
};
